import { useState } from "react";
import { ActionIcon, Card, Group, Select, Stack, Text, Tooltip } from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import { AttachmentNumber, CTC_ATTACHMENT_NUMBERS, CTC_ATTACHMENT_TITLES, ctcResolveAttachmentTemplate } from "../../../../shared/ctcAttachments";
import { CtcAttachmentNumberKey, CtcAttachmentTemplate } from "../../types";
import { CtcAttachmentPreview } from "./ctc_attachment_preview";

type Choice = Partial<Record<CtcAttachmentNumberKey, string>>;

/**
 * Right-hand panel of Back office → Customer: which text template each generated attachment uses.
 * All 5 are required. Retired templates are only listed when already chosen (and flagged).
 */
export function CtcCustomerAttachmentsPanel({ value, onChange, templates, customerName, errors }: {
    value: Choice | undefined;
    onChange: (next: Choice) => void;
    templates: (CtcAttachmentTemplate & { id: string })[];
    customerName?: string;
    errors?: Record<string, string>;
}) {
    const [preview, setPreview] = useState<AttachmentNumber | null>(null);
    const chosen = value ?? {};
    const effective = (n: AttachmentNumber) => ctcResolveAttachmentTemplate(n, { attachmentTemplateIds: chosen }, templates);

    return (
        <Card withBorder padding="md">
            <Text fw={600} mb={2}>Attachments</Text>
            <Text size="xs" c="dimmed" mb="sm">Text printed on this customer's PDFs, in the order's language. All 5 are required. Manage templates in Back office → Attachment templates.</Text>
            <Stack gap="sm">
                {CTC_ATTACHMENT_NUMBERS.map((n) => {
                    const key = String(n) as CtcAttachmentNumberKey;
                    const forNumber = templates.filter((t) => t.attachmentNumber === n && (!t.retired || t.id === chosen[key]));
                    const active = effective(n);
                    return (
                        <Group key={n} gap="xs" align="flex-end" wrap="nowrap">
                            <Select
                                style={{ flex: 1 }}
                                label={`Attachment ${n}: ${CTC_ATTACHMENT_TITLES[n]}`}
                                required
                                placeholder={forNumber.length ? "Choose a template" : "No templates yet: create one in Attachment templates"}
                                data={forNumber.map((t) => ({ value: t.id, label: `${t.name}${t.isDefault ? " (default)" : ""}${t.retired ? " (retired)" : ""}` }))}
                                value={chosen[key] ?? null}
                                onChange={(v) => {
                                    const next = { ...chosen };
                                    if (v) next[key] = v; else delete next[key];
                                    onChange(next);
                                }}
                                error={errors?.[`attachmentTemplateIds.${n}`]}
                                searchable
                            />
                            <Tooltip label={`Preview: ${active ? active.name : "built-in text"}`}>
                                <ActionIcon variant="light" size="lg" mb={1} onClick={() => setPreview(n)} aria-label={`Preview attachment ${n}`}>
                                    <IconEye size={18} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    );
                })}
            </Stack>
            {preview && (() => {
                const t = effective(preview);
                return <CtcAttachmentPreview opened onClose={() => setPreview(null)} number={preview} text={t ? { en: t.text_en, ar: t.text_ar } : undefined}
                    customer={{ name_en: customerName ?? "" }} title={`Attachment ${preview}: ${t ? `${t.name} · v${t.version}` : "built-in text"}`} />;
            })()}
        </Card>
    );
}
